import axios from "axios";
import { getAccessToken } from "utils";
import { authRouteConstants } from "constants/routeConstants";
import { API_URL, ENABLE_OFFLINE_POS } from "constants/appConstants";
import { isPublicApiPath } from "constants/publicApiPaths";
import { notify } from "../Notify";
import { setConnectivityState } from "utils/connectivityStatus";
import { logFrontendError } from "utils/errorLogger";
import {
  buildCachedResponse,
  buildOfflineQueuedResponse,
  cacheReadResponse,
  clearOfflineData,
  createClientRequestId,
  enqueueOfflineOperation,
  getCachedReadResponse,
  registerConnectivitySync,
  shouldUseOfflineQueue,
} from "utils/offlineSync";

const instance = axios.create({
  baseURL: API_URL,
  headers: { Accept: "application/json" },
  // Bounds every request so a hung backend call can no longer spin forever
  // and blank the screen. Calls that legitimately run long (bulk exports)
  // should pass their own `timeout` in the request config to override this.
  timeout: 20000,
});

// Track online state via events — navigator.onLine is unreliable in DevTools offline mode
let _appIsOnline = navigator.onLine;
window.addEventListener("online",  () => { _appIsOnline = true; });
window.addEventListener("offline", () => { _appIsOnline = false; });

// ─── bounded retry ─────────────────────────────────────────────────────────
// Safe (GET) requests only — mutations are never auto-retried, per the
// payment/order-safety rule: a retried POST could double-charge or
// double-create an order.
const RETRY_DELAYS_MS = [2000, 5000];

async function scheduleRetry(config) {
  const method = (config?.method || "get").toLowerCase();
  if (method !== "get") return false;
  const attempt = config.__retryCount || 0;
  if (attempt >= RETRY_DELAYS_MS.length) return false;
  config.__retryCount = attempt + 1;
  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
  return true;
}

// ─── request ───────────────────────────────────────────────────────────────

instance.interceptors.request.use((config) => {
  const token = getAccessToken();
  const fullPath = `${config.baseURL || ""}${config.url || ""}`;
  if (token && !isPublicApiPath(fullPath)) config.headers.Authorization = `Bearer ${token}`;

  if (ENABLE_OFFLINE_POS) {
    const method = (config.method || "get").toLowerCase();
    if (config.offlineQueue === true && ["post", "put", "patch", "delete"].includes(method)) {
      config.headers["X-Client-Request-Id"] =
        config.headers?.["X-Client-Request-Id"] || createClientRequestId();
    }
  }

  return config;
});

// ─── response ──────────────────────────────────────────────────────────────

instance.interceptors.response.use(
  function (response) {
    setConnectivityState("online");
    if (ENABLE_OFFLINE_POS) cacheReadResponse(response.config, response.data);
    return response;
  },
  async function (error) {
    const config = error?.config || {};

    // ── server responded (we have a status code) ──────────────────────────
    if (error?.response) {
      const status = error.response.status;

      if (status === 401 || status === 403) {
        if (ENABLE_OFFLINE_POS) clearOfflineData();
        notify("Authentication error.", "error");
        localStorage.removeItem("access-token");
        localStorage.removeItem("refresh-token");
        sessionStorage.clear();
        window.location.href = authRouteConstants.LOGIN;
        return false;
      }

      if (status === 502 || status === 503 || status === 504) {
        setConnectivityState("reconnecting");
        if (await scheduleRetry(config)) return instance(config);
        setConnectivityState("offline");
        logFrontendError({
          message: error.message,
          stack: error.stack,
          source: "api",
          severity: "error",
          refId: null,
        });
        notify("Webbieshop is temporarily taking longer than expected. We are reconnecting automatically.", "error");
        return false;
      }

      if (status === 413) {
        notify("File size is too large. Try with a lighter version.", "error");
        return false;
      }

      if (status === 500) {
        logFrontendError({
          message: error.message,
          stack: error.stack,
          source: "api",
          severity: "error",
          refId: null,
        });
        notify("Something went wrong.", "error");
        return false;
      }

      // 409 on a sync replay — resolve so syncOfflineQueue can detect and park the conflict
      if (status === 409 && config.__offlineReplay) {
        return Promise.resolve({ ...error.response, config });
      }

      // All other server errors — return error object to maintain backward compatibility
      // (components check `if (res === false)` not `if (!res)`)
      return error;
    }

    // ── no server response (network error / offline) ──────────────────────
    if (ENABLE_OFFLINE_POS) {
      const cached = getCachedReadResponse(config);
      if (cached) return Promise.resolve(buildCachedResponse(config, cached));

      if (shouldUseOfflineQueue(config, error)) {
        const { operation, queueDepth } = enqueueOfflineOperation(config);
        return Promise.resolve(buildOfflineQueuedResponse(config, operation, queueDepth));
      }
    }

    // Client-side timeout (ECONNABORTED) has no error.response and a message
    // like "timeout of 20000ms exceeded" — distinct from "Network Error", so
    // it needs its own branch or it silently falls through with no message.
    if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
      setConnectivityState("reconnecting");
      if (await scheduleRetry(config)) return instance(config);
      setConnectivityState("offline");
      // Only log genuine slow-backend timeouts — a timeout while the device is
      // actually offline is the expected offline case, not a bug to report.
      if (_appIsOnline && navigator.onLine) {
        logFrontendError({
          message: error.message,
          stack: error.stack,
          source: "api",
          severity: "error",
          refId: null,
        });
      }
      notify("Webbieshop is temporarily taking longer than expected. We are reconnecting automatically.", "error");
      return false;
    }

    if (error?.message === "Network Error") {
      setConnectivityState("reconnecting");
      if (await scheduleRetry(config)) return instance(config);
      setConnectivityState("offline");
      if (_appIsOnline && navigator.onLine) {
        _appIsOnline = false;
        notify("A network error occurred. Please check your internet connection.", "error");
      }
      return false;
    }

    return error;
  }
);

if (ENABLE_OFFLINE_POS) {
  registerConnectivitySync(instance);
}

export default instance;
